import React from "react";
import { getThemeValue } from "../hooks/theme";
import { Suggestion, ThemeConfig } from "../store/types";

interface FloatingBookingCtaProps {
  suggestion: Suggestion;
  buttonSize: number;
  position?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
  theme?: Partial<ThemeConfig>;
}

/** Gap between avatar edge and CTA inside the shell. */
const AVATAR_GAP = 12;

/**
 * Booking CTA stacked relative to the shell launcher (not viewport-fixed).
 * Parent `.remstal-voice-widget` owns corner placement + overflow:visible.
 */
const FloatingBookingCta: React.FC<FloatingBookingCtaProps> = ({
  suggestion,
  buttonSize,
  position = "bottom-right",
  theme,
}) => {
  if (!suggestion.action_url) {
    return null;
  }

  const actionLabel = suggestion.action_label || "Book now";
  // Sit just outside the in-flow avatar (shell height ≈ buttonSize).
  const avatarOffset = `${buttonSize + AVATAR_GAP}px`;
  const primaryColor = getThemeValue(theme, "primaryColor");
  const textColor = getThemeValue(theme, "agentMessageColor") || "#1f2937";
  const backgroundColor = getThemeValue(theme, "chatBackground") || "#ffffff";
  const borderRadius = getThemeValue(theme, "borderRadius") || "12px";

  const positionStyle: React.CSSProperties = position.includes("left")
    ? { left: 0 }
    : { right: 0 };

  if (position.startsWith("top")) {
    positionStyle.top = avatarOffset;
  } else {
    positionStyle.bottom = avatarOffset;
  }

  const handleClick = () => {
    window.open(suggestion.action_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      role="region"
      aria-label="Booking action"
      style={{
        position: "absolute",
        zIndex: 2,
        width: "min(320px, calc(100vw - 32px))",
        ...positionStyle,
        padding: "14px",
        color: textColor,
        backgroundColor,
        border: "1px solid rgba(148, 163, 184, 0.28)",
        borderRadius,
        boxShadow: "0 18px 44px rgba(15, 23, 42, 0.18)",
        fontFamily: getThemeValue(theme, "fontFamily"),
      }}
    >
      <div
        style={{
          marginBottom: "6px",
          fontSize: "14px",
          fontWeight: 700,
          lineHeight: 1.25,
        }}
      >
        {suggestion.title}
      </div>
      <div
        style={{
          marginBottom: "12px",
          fontSize: "13px",
          lineHeight: 1.4,
        }}
      >
        {suggestion.description}
      </div>
      <button
        type="button"
        onClick={handleClick}
        style={{
          width: "100%",
          minHeight: "42px",
          border: 0,
          borderRadius: "8px",
          color: "#ffffff",
          backgroundColor: primaryColor,
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: 700,
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
};

export default FloatingBookingCta;
